import { SOURCE_NAVIGATION } from '@/constants/navigation'
import type { LevelOneLink, LevelTwoLink } from '@/models/navigation'
import { reverse } from '@/utils/array'
import type { TCommon } from '~/types/app.type'

/**
 * Encapsulates all the necessary logic to render and keep track of
 * the state of the navigation.
 *
 * @returns {Object} A object with reactive properties such as
 *    - `navigation[]`: the array of navigation and subnavigation links
 *    - `activeLevelOne`: a computed reference to the active navigation link.
 *      (or "level one link").
 *    - `activeLevelTwo`: a computed reference to the active subnavigation link
 *       (or "level two link").
 *    - `levelTwoExists`: a computed value that tells if the currently active
 *       navigation link has any subnavigation.
 *    - `getLevelOnePath()`: a method that returns the full path of a navigation
 *       link.
 *    - `getLevelTwoPath()`: a method that returns the full path of a
 *       subnavigation link.
 */
export function useSourceNavigation() {
  const route = useRoute()

  const activeLevelOne = computed<LevelOneLink>(() =>
    reverse(SOURCE_NAVIGATION).find((link) => {
      return route.path.startsWith(link.href)
    }),
  )

  const levelTwoExists = computed(
    () => activeLevelOne.value && activeLevelOne.value.children,
  )

  const activeLevelTwo = computed<LevelTwoLink>(() => {
    if (!levelTwoExists.value) return

    const levelTwoLinks = activeLevelOne.value.children

    return (
      reverse(levelTwoLinks).find((link) => route.path.startsWith(link.href)) ||
      levelTwoLinks.find((link) => link.root)
    )
  })

  const getLevelOnePath = (link: LevelOneLink) => {
    const rootChild = link.children?.find((child) => child.root)
    return rootChild ? rootChild.href : link.href
  }

  const getLevelTwoPath = (link: LevelTwoLink) => {
    return link.href
  }

  return {
    navigation: computed(() => SOURCE_NAVIGATION),
    activeLevelOne,
    levelTwoExists,
    activeLevelTwo,
    getLevelOnePath,
    getLevelTwoPath,
  }
}

/**
 * Executes the passed callback every time the mobile menu's visibility
 * changes.
 *
 * @param {function} callback
 */
export function useMobileMenuVisibilityWatcher(
  callback: (..._args: TCommon | TCommon[]) => void,
) {
  const { $modal } = useNuxtApp()
  const isVisible = computed(() => $modal.mobileMenu)
  watch(isVisible, () => callback(isVisible.value))
}
